import ClearFilterButton from 'components/buttons/ClearFilterButton';
import SearchingProjectFilterDropdown from 'components/dropdowns/SearchingProjectFilterDropdown';
import TabbedUserFilterDropdown from 'components/dropdowns/TabbedUserFilterDropdown';
import styles from 'components/invoices/InvoiceQueryFilters.module.scss';
import InvoiceStatusDropdown from 'components/invoices/InvoiceStatusDropdown';
import { isEqual } from 'lodash-es';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { InvoiceQuery } from 'redux/modules/invoice.types';
import { setQuery } from 'redux/modules/invoice.types';
import type { ReportProjectIds } from 'redux/modules/superreports.types';
import type { Client } from 'types/client';
import type { InvoiceStatusFilterKeys } from 'types/invoice';
import type { EnrichedProject } from 'types/project';
import type { ThunkDispatch } from 'types/redux';
import type { User } from 'types/user';

interface Props {
  clients?: Array<Client>;
  projects?: Array<EnrichedProject>;
  users: Array<User>;
  query: InvoiceQuery;
  initialQuery: InvoiceQuery;
}

const InvoiceQueryFilters: React.FC<Props> = ({
  clients,
  projects,
  query,
  users,
  initialQuery,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const [isModified, setIsModified] = useState(false);

  React.useEffect(() => {
    setIsModified(!isEqual(initialQuery, query));
  }, [initialQuery, query]);

  function handleUserDropdownFilterChange(activeTab: 'users' | 'teams', userIds: Array<number>) {
    dispatch(setQuery({ userIds }));
  }

  function handleProjectDropdownChange(projectIds: ReportProjectIds) {
    // Convert ReportProjectIds to Array<number> for invoices
    // For invoices, we only support specific project IDs, not 'all'/'active'/'archived'
    const convertedProjectIds = Array.isArray(projectIds) ? projectIds : [];
    dispatch(setQuery({ projectIds: convertedProjectIds }));
  }

  function setStatusQuery(status: InvoiceStatusFilterKeys) {
    dispatch(setQuery({ status }));
  }

  function onClearQuery() {
    dispatch(setQuery(initialQuery));
  }

  return (
    <div className={styles.container}>
      <InvoiceStatusDropdown status={query.status} setQuery={setStatusQuery} />
      {clients && projects ? (
        <SearchingProjectFilterDropdown
          unrestrictedWhenEmpty
          selected={query.projectIds}
          projects={projects}
          clients={clients}
          onChange={handleProjectDropdownChange}
        />
      ) : null}
      <TabbedUserFilterDropdown
        unrestrictedWhenEmpty
        onChange={handleUserDropdownFilterChange}
        selectedUsers={query.userIds}
        users={users}
      />
      {isModified && (
        <ClearFilterButton onClick={onClearQuery} testId="clear-invoice-query-filter" />
      )}
    </div>
  );
};

export default InvoiceQueryFilters;
